import React, { useRef, useState, useEffect, useMemo } from "react";
import {
  motion,
  useScroll,
  useTransform,
  AnimatePresence,
} from "framer-motion";
import {
  Target,
  Flag,
  Milestone,
  Calendar,
  CheckCircle,
  TrendingUp,
  BarChart,
  Heading1,
  ArrowRight,
  ChevronRight,
  ChevronLeft,
  Clock,
  DollarSign,
  Users,
  Sparkles,
  Zap,
  ClipboardList,
  MessageCircle,
} from "lucide-react";
import OriginalResponsesDisplay from "./OriginalResponsesDisplay";
import ScaleBar from "./ScaleBar";

// Interactive Project Goal Card
const ProjectGoalCard = ({
  title,
  value,
  icon: Icon,
  index,
  isExpanded,
  onToggle,
  palette,
  questionTitles = {},
}) => {
  const delayAnimation = index * 0.1;

  // Different background colors for visual variety
  const bgGradients = [
    "linear-gradient(135deg, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0.03) 100%)",
    "linear-gradient(135deg, rgba(var(--primary-color-rgb), 0.12) 0%, rgba(var(--primary-color-rgb), 0.06) 100%)",
    "linear-gradient(135deg, rgba(var(--secondary-color-rgb), 0.12) 0%, rgba(var(--secondary-color-rgb), 0.06) 100%)",
    "linear-gradient(135deg, rgba(var(--accent-color-rgb), 0.12) 0%, rgba(var(--accent-color-rgb), 0.06) 100%)",
  ];

  const gradient = bgGradients[index % bgGradients.length];

  // Detect if the value contains list items
  const detectListItems = (text) => {
    if (!text || typeof text !== "string") return null;

    // Check for numbered lists, bullet points, or dash-prefixed items
    const listPatterns = [
      /^\s*\d+\.\s+(.+)$/gm, // Numbered items like "1. Item"
      /^\s*[-•*]\s+(.+)$/gm, // Bulleted items
      /^\s*-\s+(.+)$/gm, // Dash items
      /^\s*[a-z][\)\.]\s+(.+)$/gm, // Letter items like "a) Item" or "a. Item"
    ];

    const hasListPattern = listPatterns.some((pattern) => pattern.test(text));

    if (hasListPattern) {
      // Split by new lines and process each line
      const lines = text.split(/\r?\n/).filter((line) => line.trim());
      return lines.map((line) => {
        return line
          .replace(/^\s*\d+\.\s+/, "")
          .replace(/^\s*[-•*]\s+/, "")
          .replace(/^\s*-\s+/, "")
          .replace(/^\s*[a-z][\)\.]\s+/, "")
          .trim();
      });
    }

    // If it contains several short phrases separated by commas, it might be a list
    if (
      text.includes(",") &&
      text.split(",").length > 2 &&
      text.split(",").every((item) => item.trim().length < 50)
    ) {
      return text.split(",").map((item) => item.trim());
    }

    return null;
  };

  // Format the text for better readability
  const formatText = (text) => {
    if (!text) return ["No information available"];

    if (typeof text !== "string") {
      if (typeof text === "object") {
        try {
          return [JSON.stringify(text, null, 2)];
        } catch (e) {
          return ["Complex object"];
        }
      }
      return [String(text)];
    }

    // If text is shorter than 200 chars, don't split it
    if (text.length < 200) return [text];

    // Split by sentences for better paragraph breaks
    const sentences = text.split(/(?<=[.!?])\s+/);
    const paragraphs = [];
    let currentParagraph = "";

    sentences.forEach((sentence) => {
      if (currentParagraph.length + sentence.length > 250) {
        paragraphs.push(currentParagraph);
        currentParagraph = sentence;
      } else {
        currentParagraph += (currentParagraph ? " " : "") + sentence;
      }
    });

    if (currentParagraph) {
      paragraphs.push(currentParagraph);
    }

    return paragraphs;
  };

  // Extract potential timeline information
  const extractTimeline = (text) => {
    if (!text) return null;

    // If the title suggests this is already a timeline, return the whole text
    if (
      title.toLowerCase().includes("timeline") ||
      title.toLowerCase().includes("deadline") ||
      title.toLowerCase().includes("schedule")
    ) {
      return text;
    }

    // Check for date patterns in the text
    const dateRegex =
      /(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4}|\w+ \d{1,2}(?:st|nd|rd|th)?,? \d{4}?|\w+ \d{4})/gi;
    const dates = text.match(dateRegex);

    if (dates && dates.length > 0) {
      return text;
    }

    return null;
  };

  // Extract metrics or success criteria
  const extractMetrics = (text) => {
    if (!text) return null;

    // If it's already a list of items, those might be metrics
    const listItems = detectListItems(text);
    if (
      listItems &&
      (title.toLowerCase().includes("metric") ||
        title.toLowerCase().includes("kpi") ||
        title.toLowerCase().includes("success") ||
        title.toLowerCase().includes("measure"))
    ) {
      return listItems;
    }

    return null;
  };

  // Check if this value contains list items
  const listItems = detectListItems(value);

  // Extract timeline and metrics if available
  const timeline = extractTimeline(value);
  const metrics = extractMetrics(value);

  // If not a list, format as paragraphs
  const paragraphs = !listItems ? formatText(value) : null;

  // Determine if this goal has high priority based on title
  const isPriority =
    title.toLowerCase().includes("primary") ||
    title.toLowerCase().includes("main") ||
    title.toLowerCase().includes("key");

  // Use ScaleBar for 1-10 scale values
  const isScaleValue = (() => {
    let num = value;
    if (typeof value === "string") num = parseFloat(value);
    return typeof num === "number" && num >= 1 && num <= 10;
  })();

  // Helper to get left/right labels for scale
  const getScaleLabels = (title, value) => {
    if (
      typeof value === "object" &&
      value !== null &&
      (value.leftLabel || value.rightLabel)
    ) {
      return {
        leftLabel: value.leftLabel || "",
        rightLabel: value.rightLabel || "",
      };
    }
    const lowerTitle = title.toLowerCase();
    if (lowerTitle.includes("formal") || lowerTitle.includes("casual")) {
      return { leftLabel: "Casual", rightLabel: "Formal" };
    }
    if (lowerTitle.includes("playful") || lowerTitle.includes("serious")) {
      return { leftLabel: "Serious", rightLabel: "Playful" };
    }
    if (lowerTitle.includes("modern") || lowerTitle.includes("traditional")) {
      return { leftLabel: "Traditional", rightLabel: "Modern" };
    }
    if (lowerTitle.includes("loud") || lowerTitle.includes("quiet")) {
      return { leftLabel: "Quiet", rightLabel: "Loud" };
    }
    if (lowerTitle.includes("bright") || lowerTitle.includes("dark")) {
      return { leftLabel: "Dark", rightLabel: "Bright" };
    }
    return { leftLabel: "Low", rightLabel: "High" };
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      onClick={onToggle}
      className={`rounded-xl overflow-hidden backdrop-blur-sm border border-white/10 cursor-pointer transition-all duration-300 ${
        isExpanded ? "col-span-full" : ""
      }`}
      style={{
        background: gradient,
      }}
      whileHover={{
        scale: isExpanded ? 1 : 1.02,
        boxShadow: "0 5px 15px rgba(0, 0, 0, 0.1)",
      }}
    >
      <motion.div
        className="p-6"
        initial={false}
        animate={{
          padding: isExpanded ? "1.5rem" : "1.5rem",
        }}
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <motion.div
              className="w-10 h-10 rounded-full flex items-center justify-center mr-3 text-white"
              style={{ backgroundColor: "var(--primary-color)" }}
              whileHover={{ scale: 1.1 }}
            >
              <Icon size={20} />
            </motion.div>
            <h3 className="text-xl font-bold text-white">{title}</h3>
          </div>
          <motion.div
            animate={{ rotate: isExpanded ? 90 : 0 }}
            transition={{ duration: 0.3 }}
            className="text-white/60"
          >
            <ArrowRight size={20} />
          </motion.div>
        </div>

        {/* Priority or Status label */}
        {isPriority && (
          <div className="ml-0 mt-1.5 mb-3 inline-flex items-center px-3 py-1.5 rounded-full bg-yellow-400/20">
            <Flag size={14} className="text-yellow-400 mr-1.5" />
            <span className="text-xs text-yellow-400 font-medium">
              High Priority
            </span>
          </div>
        )}

        {title.toLowerCase().includes("status") && (
          <div className="ml-0 mt-1.5 mb-3 inline-flex items-center px-3 py-1.5 rounded-full bg-green-400/20">
            <CheckCircle size={14} className="text-green-400 mr-1.5" />
            <span className="text-xs text-green-400 font-medium">Status</span>
          </div>
        )}

        <AnimatePresence>
          {!isExpanded && (
            <motion.div
              className="text-white/70"
              exit={{ opacity: 0, height: 0 }}
            >
              {isScaleValue
                ? (() => {
                    let num = value;
                    let leftLabel, rightLabel;
                    if (
                      typeof value === "object" &&
                      value !== null &&
                      typeof value.value === "number"
                    ) {
                      num = value.value;
                      leftLabel = value.leftLabel;
                      rightLabel = value.rightLabel;
                    } else {
                      if (typeof value === "string") num = parseFloat(value);
                      const labels = getScaleLabels(title, value);
                      leftLabel = labels.leftLabel;
                      rightLabel = labels.rightLabel;
                    }
                    const notes = typeof value === "object" && value !== null ? value.notes : null;
                    const notesVisible = typeof value === "object" && value !== null ? value.notesVisible !== false : true;
                    return (
                      <div>
                        <ScaleBar
                          value={num}
                          min={1}
                          max={10}
                          leftLabel={leftLabel}
                          rightLabel={rightLabel}
                          palette={palette}
                          label={title}
                          customTitle={questionTitles[title]}
                        />
                        {notes && notesVisible && (
                          <div className="mt-3 p-3 bg-white/5 rounded-lg border border-white/10">
                            <p className="text-white/70 text-sm whitespace-pre-wrap">{notes}</p>
                          </div>
                        )}
                      </div>
                    );
                  })()
                : listItems
                ? `${listItems.length} items identified`
                : (() => {
                    // Safely convert value to string for display
                    if (typeof value === "object" && value !== null) {
                      if (typeof value.value !== "undefined") {
                        return String(value.value);
                      }
                      if (value.description) {
                        return value.description;
                      }
                      try {
                        return JSON.stringify(value);
                      } catch (e) {
                        return "Complex object";
                      }
                    }
                    return value ? String(value) : "No information available";
                  })()}
            </motion.div>
          )}

          {isExpanded && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-4"
            >
              {isScaleValue ? (
                (() => {
                  let num = value;
                  let leftLabel, rightLabel;
                  if (
                    typeof value === "object" &&
                    value !== null &&
                    typeof value.value === "number"
                  ) {
                    num = value.value;
                    leftLabel = value.leftLabel;
                    rightLabel = value.rightLabel;
                  } else {
                    if (typeof value === "string") num = parseFloat(value);
                    const labels = getScaleLabels(title, value);
                    leftLabel = labels.leftLabel;
                    rightLabel = labels.rightLabel;
                  }
                  const notes = typeof value === "object" && value !== null ? value.notes : null;
                  const notesVisible = typeof value === "object" && value !== null ? value.notesVisible !== false : true;
                  return (
                    <div>
                      <ScaleBar
                        value={num}
                        min={1}
                        max={10}
                        leftLabel={leftLabel}
                        rightLabel={rightLabel}
                        palette={palette}
                        label={title}
                      />
                      {notes && notesVisible && (
                        <div className="mt-3 p-3 bg-white/5 rounded-lg border border-white/10">
                          <p className="text-white/70 text-sm whitespace-pre-wrap">{notes}</p>
                        </div>
                      )}
                    </div>
                  );
                })()
              ) : listItems ? (
                <ul className="space-y-2 pl-2">
                  {listItems.map((item, i) => (
                    <motion.li
                      key={i}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="flex items-start text-white/80"
                    >
                      <span className="inline-block w-4 h-4 rounded-full mr-3 mt-1 flex-shrink-0 bg-white/10"></span>
                      <span>
                        {typeof item === "object" && item !== null
                          ? typeof item.value !== "undefined"
                            ? String(item.value)
                            : JSON.stringify(item)
                          : String(item)}
                      </span>
                    </motion.li>
                  ))}
                </ul>
              ) : (
                <div className="space-y-4">
                  {paragraphs &&
                    paragraphs.map((paragraph, i) => (
                      <motion.p
                        key={i}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.1 }}
                        className="text-white/80 leading-relaxed"
                      >
                        {paragraph}
                      </motion.p>
                    ))}
                </div>
              )}

              {/* Timeline if available and not already displayed */}
              {timeline && !listItems && !paragraphs[0].includes(timeline) && (
                <motion.div
                  className="flex items-center gap-3 mt-4 px-4 py-3 rounded-lg bg-white/5 border border-white/10"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                >
                  <Calendar size={18} className="text-[var(--primary-color)]" />
                  <div>
                    <h4 className="text-sm font-medium text-white/80">
                      Timeline
                    </h4>
                    <p className="text-white/90">{timeline}</p>
                  </div>
                </motion.div>
              )}

              {/* Success Metrics if available */}
              {metrics && metrics.length > 0 && (
                <motion.div
                  className="space-y-3 mt-4"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4 }}
                >
                  <h4 className="text-md font-medium text-white flex items-center gap-2">
                    <TrendingUp
                      size={16}
                      className="text-[var(--primary-color)]"
                    />
                    Success Metrics
                  </h4>
                  <div className="grid grid-cols-1 gap-2">
                    {metrics.map((metric, i) => (
                      <div
                        key={i}
                        className="p-3 rounded-lg border border-white/10 bg-white/5 flex items-center gap-3"
                      >
                        <div className="w-6 h-6 rounded-full bg-[var(--primary-color-30)] flex items-center justify-center">
                          <CheckCircle size={14} className="text-white" />
                        </div>
                        <span className="text-sm text-white/80">{metric}</span>
                      </div>
                    ))}
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

// Project Timeline Visualization Component
const ProjectTimeline = ({ title, deadlines }) => {
  // Parse deadlines to extract milestones
  const milestones = useMemo(() => {
    if (!deadlines) return [];

    // Try to extract dates and milestones from text
    const dateRegex =
      /(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4}|\w+ \d{1,2}(?:st|nd|rd|th)?,? \d{4}?|\w+ \d{4})/gi;

    // Try to find dates in the text
    const dates = deadlines.match(dateRegex) || [];

    // If we found dates, create milestone objects
    if (dates.length > 0) {
      const milestoneItems = [];

      // Extract text segments around dates to use as descriptions
      let lastIndex = 0;
      let text = deadlines;

      dates.forEach((date, index) => {
        const dateIndex = text.indexOf(date, lastIndex);
        if (dateIndex !== -1) {
          // Get text before this date (but after previous date) as description
          let description = text.substring(lastIndex, dateIndex).trim();

          // Clean up description
          description = description.replace(/^[:\-–—,;.]+/, "").trim();

          if (description) {
            milestoneItems.push({
              date,
              description,
            });
          } else if (index > 0) {
            // If no description, associate with previous milestone
            milestoneItems.push({
              date,
              description: "Next milestone",
            });
          }

          lastIndex = dateIndex + date.length;
        }
      });

      // Get any remaining text after the last date
      const remainingText = text.substring(lastIndex).trim();
      if (remainingText && remainingText.length > 3) {
        milestoneItems.push({
          date: "Final Completion",
          description: remainingText.replace(/^[:\-–—,;.]+/, "").trim(),
        });
      }

      return milestoneItems;
    }

    // If no dates found, create a single milestone with the full text
    return [
      {
        date: "Project Timeline",
        description: deadlines,
      },
    ];
  }, [deadlines]);

  // If no valid deadlines, don't render
  if (!deadlines) return null;

  return (
    <motion.div
      className="p-6 rounded-xl border border-white/10 bg-white/5 backdrop-blur-md mt-10"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
      whileHover={{ scale: 1.02 }}
    >
      <div className="flex items-center gap-3 mb-5">
        <div
          className="p-2.5 rounded-lg"
          style={{ backgroundColor: "var(--primary-color-20)" }}
        >
          <Calendar size={20} className="text-white" />
        </div>
        <h3 className="text-xl font-bold text-white">{title}</h3>
      </div>

      {/* Timeline Visualization */}
      <div className="relative pl-6 mt-8 space-y-8 before:absolute before:top-0 before:bottom-0 before:left-[11px] before:w-[2px] before:bg-gradient-to-b before:from-[var(--primary-color)] before:to-[var(--accent-color)]">
        {milestones.map((milestone, index) => (
          <motion.div
            key={index}
            className="relative"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.5 + index * 0.1 }}
          >
            {/* Milestone dot */}
            <div className="absolute top-1 left-[-24px] w-6 h-6 rounded-full bg-[var(--primary-color)] shadow-lg shadow-[var(--primary-color-30)] flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-white" />
            </div>

            {/* Milestone content */}
            <div>
              <h4 className="text-white font-semibold mb-1">
                {milestone.date}
              </h4>
              <p className="text-white/70 text-sm">{milestone.description}</p>
            </div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

const DynamicProjectGoalsSection = ({
  goals,
  scrollProgress,
  palette,
  profileId,
  backgroundImage,
  sectionIndex = 0,
  questionTitles = {},
  responseData,
  questionnaireData,
}) => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });

  const [expandedCardIndex, setExpandedCardIndex] = useState(null);
  const [activeSlide, setActiveSlide] = useState(0);

  // Enhanced animation controls
  const opacity = useTransform(scrollYProgress, [0, 0.2, 0.8, 1], [0, 1, 1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.95, 1, 0.95]);
  const bgOpacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.7, 0.9, 0.7]);

  // Determine if this section should have the background on the left or right
  const isLeftAligned = sectionIndex % 2 === 0;

  // Cards per slide for mobile view
  const CARDS_PER_SLIDE = 2;

  // Set up CSS Variables for colors
  useEffect(() => {
    if (palette) {
      document.documentElement.style.setProperty(
        "--primary-color",
        palette.primary
      );
      document.documentElement.style.setProperty(
        "--secondary-color",
        palette.secondary
      );
      document.documentElement.style.setProperty(
        "--accent-color",
        palette.accent
      );

      // Helper function to convert hex to RGB for gradients
      const hexToRgb = (hex) => {
        // Remove # if present
        hex = hex.replace("#", "");

        // Convert 3-digit hex to 6-digits
        if (hex.length === 3) {
          hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
        }

        // Extract r, g, b values
        const r = parseInt(hex.substring(0, 2), 16);
        const g = parseInt(hex.substring(2, 4), 16);
        const b = parseInt(hex.substring(4, 6), 16);

        return `${r}, ${g}, ${b}`;
      };

      // Set RGB values for CSS variables
      try {
        document.documentElement.style.setProperty(
          "--primary-color-rgb",
          hexToRgb(palette.primary)
        );
        document.documentElement.style.setProperty(
          "--secondary-color-rgb",
          hexToRgb(palette.secondary)
        );
        document.documentElement.style.setProperty(
          "--accent-color-rgb",
          hexToRgb(palette.accent)
        );

        // Add color variants
        document.documentElement.style.setProperty(
          "--primary-color-20",
          `rgba(${hexToRgb(palette.primary)}, 0.2)`
        );
        document.documentElement.style.setProperty(
          "--primary-color-30",
          `rgba(${hexToRgb(palette.primary)}, 0.3)`
        );
      } catch (e) {
        console.error("Error setting RGB color values:", e);
      }
    }
  }, [palette]);

  // Define icons for different goal types
  const icons = {
    "Project Goals": Target,
    "Business Objectives": TrendingUp,
    KPIs: BarChart,
    Milestones: Milestone,
    Timeline: Calendar,
    "Project Scope": Flag,
    "Success Criteria": CheckCircle,
    Priorities: Heading1,
    Deadlines: Clock,
    Budget: DollarSign,
    Team: Users,
    Process: ClipboardList,
    Requirements: ClipboardList,
  };

  // Find goal data that might contain a timeline
  const findTimelineData = () => {
    for (const [key, value] of Object.entries(goals)) {
      const lowerKey = key.toLowerCase();
      if (
        (lowerKey.includes("timeline") ||
          lowerKey.includes("deadline") ||
          lowerKey.includes("schedule")) &&
        value
      ) {
        return {
          key,
          value,
        };
      }
    }

    // If no dedicated timeline field, look for date patterns in any field
    for (const [key, value] of Object.entries(goals)) {
      if (typeof value === "string") {
        const dateRegex =
          /(\d{1,2}[\/\-\.]\d{1,2}[\/\-\.]\d{2,4}|\w+ \d{1,2}(?:st|nd|rd|th)?,? \d{4}?|\w+ \d{4})/gi;
        const dates = value.match(dateRegex);
        if (dates && dates.length > 0) {
          return {
            key,
            value,
          };
        }
      }
    }

    return null;
  };

  // Map goals data to cards with appropriate icons
  const getGoalCards = () => {
    return Object.entries(goals).map(([key, value], index) => {
      // Use displayTitle if available (for duplicate titles with question IDs)
      const displayKey = (typeof value === "object" && value !== null && value.displayTitle) 
        ? value.displayTitle 
        : key;
      
      // Find exact match first
      let Icon = icons[displayKey];

      // If no exact match, try to find partial match
      if (!Icon) {
        const lowerKey = displayKey.toLowerCase();
        for (const [iconKey, iconComponent] of Object.entries(icons)) {
          if (lowerKey.includes(iconKey.toLowerCase())) {
            Icon = iconComponent;
            break;
          }
        }
      }

      // Default to Target icon if no match found
      Icon = Icon || Target;

      return {
        title: displayKey, // Use display title for duplicate titles
        value: value,
        icon: Icon,
        index,
      };
    });
  };

  const goalCards = getGoalCards();

  // Filter out the timeline card since we'll show it separately
  const timelineData = findTimelineData();
  const filteredGoalCards = timelineData
    ? goalCards.filter((card) => card.title !== timelineData.key)
    : goalCards;

  // Group items into slides for mobile navigation
  const itemSlides = [];
  for (let i = 0; i < filteredGoalCards.length; i += CARDS_PER_SLIDE) {
    itemSlides.push(filteredGoalCards.slice(i, i + CARDS_PER_SLIDE));
  }

  const toggleCard = (index) => {
    setExpandedCardIndex(expandedCardIndex === index ? null : index);
  };

  const nextSlide = () => {
    setActiveSlide((prev) => (prev < itemSlides.length - 1 ? prev + 1 : 0));
  };

  const prevSlide = () => {
    setActiveSlide((prev) => (prev > 0 ? prev - 1 : itemSlides.length - 1));
  };

  return (
    <motion.section
      ref={sectionRef}
      className="min-h-screen relative flex items-center justify-center py-24 overflow-hidden"
      style={{
        opacity,
        backgroundColor: "rgba(5, 8, 15, 0.95)", // Much darker background
      }}
    >
      {/* Background image with transparency mask blending */}
      {backgroundImage && (
        <div className="absolute inset-0 w-full h-full overflow-hidden">
          <motion.div
            className={`absolute inset-0 h-full bg-center bg-cover bg-no-repeat`}
            style={{
              backgroundImage: `url(${backgroundImage})`,
              opacity: bgOpacity,
              filter: "contrast(1.2) brightness(1.15)",
              mixBlendMode: "soft-light",
              width: "75%",
              left: isLeftAligned ? "0" : "auto",
              right: isLeftAligned ? "auto" : "0",
              maskImage: isLeftAligned
                ? "linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)"
                : "linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)",
              WebkitMaskImage: isLeftAligned
                ? "linear-gradient(to right, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)"
                : "linear-gradient(to left, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 65%, rgba(0,0,0,0.9) 75%, rgba(0,0,0,0.6) 85%, rgba(0,0,0,0.2) 95%, rgba(0,0,0,0) 100%)",
            }}
          />
        </div>
      )}

      {/* Content container with proper positioning */}
      <div
        className={`relative z-10 w-full max-w-7xl mx-auto px-4 ${
          isLeftAligned ? "ml-auto" : "mr-auto"
        }`}
      >
        <motion.h2
          className="text-5xl md:text-7xl mb-16 font-bold text-white"
          style={{ textShadow: "0 2px 6px rgba(0, 0, 0, 0.5)" }}
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          Project Goals
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          viewport={{ once: true }}
          className="text-white/60 max-w-3xl mb-12 text-xl"
        >
          The key objectives and expected outcomes for this brand development
          project.
        </motion.p>

        {/* AI-Generated Narrative Section */}
        {profileId && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <div className="flex items-center mb-6">
              <motion.div
                className="w-12 h-12 rounded-full flex items-center justify-center mr-4 text-white"
                style={{ backgroundColor: "var(--primary-color)" }}
                whileHover={{ scale: 1.1 }}
              >
                <MessageCircle size={24} />
              </motion.div>
              <h3 className="text-3xl font-bold text-white">Goals Analysis</h3>
            </div>

            <OriginalResponsesDisplay
              sectionType="projectGoals"
              responseData={responseData}
              questionnaireData={questionnaireData}
              textColor="rgba(255, 255, 255, 0.9)"
              accentColor="var(--primary-color)"
            />
          </motion.div>
        )}

        {/* Main Content */}
        <div className="space-y-10">
          {/* Desktop View: Goals Cards Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-min">
            {filteredGoalCards.map((card, index) => (
              <ProjectGoalCard
                key={`goal-${index}`}
                title={card.title}
                value={card.value}
                icon={card.icon}
                index={index}
                isExpanded={expandedCardIndex === index}
                onToggle={() => toggleCard(index)}
                palette={palette}
                questionTitles={questionTitles}
              />
            ))}
          </div>

          {/* Mobile View: Interactive Cards Carousel */}
          <div className="md:hidden relative">
            <div className="flex justify-between items-center mb-4">
              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={prevSlide}
                className="bg-white/10 rounded-full p-2"
                disabled={itemSlides.length <= 1}
              >
                <ChevronLeft
                  className={`${
                    itemSlides.length <= 1 ? "text-white/30" : "text-white"
                  }`}
                  size={20}
                />
              </motion.button>

              <div className="text-white/60 text-sm">
                {itemSlides.length > 0
                  ? `${activeSlide + 1} / ${itemSlides.length}`
                  : "0 / 0"}
              </div>

              <motion.button
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={nextSlide}
                className="bg-white/10 rounded-full p-2"
                disabled={itemSlides.length <= 1}
              >
                <ChevronRight
                  className={`${
                    itemSlides.length <= 1 ? "text-white/30" : "text-white"
                  }`}
                  size={20}
                />
              </motion.button>
            </div>

            <AnimatePresence mode="wait">
              {itemSlides.length > 0 ? (
                <motion.div
                  key={activeSlide}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                  className="space-y-4"
                >
                  {itemSlides[activeSlide].map((card, index) => (
                    <ProjectGoalCard
                      key={index}
                      title={card.title}
                      value={card.value}
                      icon={card.icon}
                      index={index + activeSlide * CARDS_PER_SLIDE}
                      isExpanded={
                        expandedCardIndex ===
                        index + activeSlide * CARDS_PER_SLIDE
                      }
                      onToggle={() =>
                        toggleCard(index + activeSlide * CARDS_PER_SLIDE)
                      }
                      palette={palette}
                      questionTitles={questionTitles}
                    />
                  ))}
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-8 text-center bg-white/5 rounded-lg border border-white/10"
                >
                  <p className="text-white/70">
                    No content is available to display.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Pagination dots - only show if there are multiple slides */}
            {itemSlides.length > 1 && (
              <div className="flex justify-center mt-6 space-x-2">
                {itemSlides.map((_, index) => (
                  <motion.button
                    key={index}
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor:
                        activeSlide === index
                          ? "var(--primary-color)"
                          : "rgba(255, 255, 255, 0.3)",
                    }}
                    onClick={() => setActiveSlide(index)}
                    whileHover={{ scale: 1.3 }}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Project Timeline Visualization */}
          {timelineData && (
            <ProjectTimeline
              title={timelineData.key}
              deadlines={timelineData.value}
            />
          )}
        </div>
      </div>
    </motion.section>
  );
};

export default DynamicProjectGoalsSection;
